
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, message, Modal, Spin, Alert, Typography, Segmented } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Liability, LiabilitiesResponse, Asset } from '../../types';
import LiabilityDetails from './liabilitydetail';
import AddEditLiabilityForm from './liabilityform';
import { useGlobalContext } from '../../store/GlobalContext';
import { LiabilityService } from './liability.service';

const LiabilitiesListPage: React.FC = () => {
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [totalLiabilities, setTotalLiabilities] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [selectedLiability, setSelectedLiability] = useState<Liability | null>(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState<boolean>(false);
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  // Fetch liabilities when component mounts or pagination changes
  useEffect(() => {
    if (estateId) {
      fetchLiabilities(currentPage, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, estateId, status]);

  const fetchLiabilities = async (page: number, limit: number) => {
    setLoading(true);
    setError('');
    try {
      const response: LiabilitiesResponse = await LiabilityService.fetchLiabilities({ estateId, page, limit, status });
      setLiabilities(response.data);
      setTotalLiabilities(response.total);
      setCurrentPage(response.page);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to fetch liabilities.');
    } finally {
      setLoading(false);
    }
  };

  const showLiabilityDetails = (liability: Liability) => {
    setSelectedLiability(liability);
    setIsDetailsModalVisible(true);
  };

  const closeLiabilityDetails = () => {
    setSelectedLiability(null);
    setIsDetailsModalVisible(false);
  };

  const showAddForm = () => {
    setSelectedLiability(null);
    setIsFormVisible(true);
  };

  const closeAddEditForm = () => {
    setSelectedLiability(null);
    setIsFormVisible(false);
  };

  const handleAddEditLiability = async (liability: Liability) => {
    setFormLoading(true);
    try {
      if (liability.uid) {
        // Update existing liability

        const {estateId, uid, ...liabilityData } = liability;
        await LiabilityService.updateLiability(estateId, uid, liabilityData);
        message.success('Liability updated successfully.');
      } else {
        // Create new liability
        await LiabilityService.createLiability(estateId, liability);
        message.success('Liability added successfully.');
      }
      fetchLiabilities(currentPage, pageSize);
      closeAddEditForm();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to save liability.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteLiability = async (liabilityId: string) => {
    try {
      await LiabilityService.deleteLiability(estateId, liabilityId);
      message.success('Liability deleted successfully.');
      fetchLiabilities(currentPage, pageSize);
      closeLiabilityDetails();
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to delete liability.');
    }
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns = [
    { title: 'Unsecured claim', dataIndex: 'unsecuredAmount', key: 'unsecuredAmount', render: (value: number | null) => value == null ? 'Not recorded' : Number(value).toLocaleString() },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Outstanding balance', dataIndex: 'outstandingBalance', key: 'outstandingBalance', render: (value: number | null) => value == null ? 'Not recorded' : Number(value).toLocaleString() },

    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 90,
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      key: 'assetName',
      // A liability need not be secured on anything, so the asset is nullable.
      render: (asset: Asset | null) => asset?.name ?? '',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Monthly Payment',
      dataIndex: 'amount',
      key: 'monthlyPayment',
      render: (value: number) => `$${value.toLocaleString()}`,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
    },
    {
      title: 'Date Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : ''),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Liability) => (
        <Button type="link" onClick={() => showLiabilityDetails(record)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Liabilities List</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={showAddForm}>
              Add Liability
            </Button>
          </div>
        }
        style={{ marginTop: '24px' }}
        extra={null}
      >
        <Typography >
          View and manage all liabilities associated with your estate.
        </Typography>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ margin: '16px 0' }}
          />
        )}
        <Segmented value={status} options={[{value:'open', label:'Open liabilities'}, {value:'closed', label:'Closed / court accounting file'}]} onChange={value => { setStatus(value as 'open' | 'closed'); setCurrentPage(1); }} style={{margin: '16px 0'}} />
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={liabilities}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalLiabilities,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50'],
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
          />
        </Spin>
      </Card>

      {/* Add/Edit Liability Form Modal */}
      <AddEditLiabilityForm
        visible={isFormVisible}
        onClose={closeAddEditForm}
        initialData={selectedLiability || undefined}
        onSubmit={handleAddEditLiability}
        estateId={estateId}
      />

      {/* Liability Details Modal */}
      <LiabilityDetails
        visible={isDetailsModalVisible}
        onClose={closeLiabilityDetails}
        liability={selectedLiability}
        onEdit={(liability: Liability) => {
          setSelectedLiability(liability);
          setIsFormVisible(true);
        }}
        onDelete={handleDeleteLiability}
      />
    </div>
  );
};

export default LiabilitiesListPage;
